// src/mls-membership-probe.ts
/**
 * Map a `GET /api/v1/mls/groups/{id}` status onto "do I still hold state for
 * this group?" — LEAF MODULE (#629).
 *
 * `quarantineOrphanedMlsState` takes this decision as an injected dependency
 * precisely so its failure modes stay testable without a network. This is that
 * decision, isolated so the mapping itself is pinned.
 *
 * ⚠️ The load-bearing case is `null`. The quarantine treats `null` as "leave it
 * alone" and only acts on an explicit `false`, because a transient API failure
 * that looks like "you were removed" is how a working agent loses its history.
 * Every status we do not positively understand must therefore map to `null`,
 * never to `false`.
 *
 * Backend semantics (app/api/v1/mls.py:457):
 *   200  membership (or A2A observer) confirmed  -> keep
 *   404  no such group at all                    -> definitively gone
 *   403  group exists, caller is not a member    -> definitively not ours
 *   else unknown                                 -> null
 *
 * 401 is deliberately `null`, not `false`: an expired device JWT is an auth
 * problem, not a statement about membership, and treating it as removal would
 * quarantine every group on the device at once.
 */

/** `true` keep · `false` quarantine · `null` unknown, do nothing. */
export type MembershipVerdict = boolean | null;

export function mlsMembershipFromStatus(status: number): MembershipVerdict {
  if (status === 200) return true;
  if (status === 403 || status === 404) return false;
  return null;
}
